import React, { useMemo } from 'react';
import { GetStaticProps, GetStaticPaths, NextPage } from 'next';
import config from '@/lib/config';
import { IPlayer } from '@/models/player';
import {
	getActiveTeamData,
	getCareerStats,
	getCurrentYearCarreerStats,
	getPassportData,
	getSeasonStats,
} from '@/utils/playerDataTransformer';
import { IStandings } from '@/models/standings';
import { fetchAPI, getPrimaryMenuItems } from '@/lib/api';

import Layout from '@/components/layout';
import Head from 'next/head';
import Image from 'next/image';
import Container from '@/components/container';
import { Text, H2, H3, H5, H6, P, Label } from '@/components/text';
import { Disclosure } from '@headlessui/react';
import { ChevronUpIcon } from '@heroicons/react/20/solid';
import NavBar from '@/components/navbar';
import { IMenuItem } from '@/models/menu-item';
import { ISponsor } from '@/models/sponsor';
import classNames from 'classnames';

interface PlayerPageProps {
	primaryMenuItems: IMenuItem[];
	sponsors: ISponsor[];
	player: IPlayer;
	error?: string;
	passportData: ReturnType<typeof getPassportData>;
	careerStatsData: ReturnType<typeof getCareerStats>;
	seasonStatsData: ReturnType<typeof getSeasonStats>;
	currentYearCareerStats: ReturnType<typeof getCurrentYearCarreerStats>;
	activeTeam: ReturnType<typeof getActiveTeamData>;
}

const CareerData: React.FC<{
	goals: number;
	appearances: number;
	contestantName: string;
	assists: number;
	yellowCards: number;
	redCards: number;
	startDate: string;
	endDate: string;
}> = ({ goals, appearances, contestantName, assists, yellowCards, redCards, startDate, endDate }) => {
	return (
		<ul role="list" className="w-full  px-4 py-3">
			<li className="pb-2.5 w-full flex items-center gap-x-3">
				<P className="flex-auto truncate">Club</P>
				<Label className="truncate flex-none ">{contestantName}</Label>
			</li>
			<li className="pb-2.5 w-full flex items-center gap-x-3">
				<P className="flex-auto truncate">Gespeeld</P>
				<Label className="truncate flex-none ">{appearances}</Label>
			</li>

			<li className="pb-2.5 w-full flex items-center gap-x-3">
				<P className="flex-auto truncate">Goals</P>
				<Label className="truncate flex-none">{goals}</Label>
			</li>
			<li className="pb-2.5 w-full flex items-center gap-x-3">
				<P className="flex-auto truncate">Assists</P>
				<Label className="truncate flex-none">{assists}</Label>
			</li>
			<li className="pb-2.5 w-full flex items-center gap-x-3">
				<P className="flex-auto truncate">Gele kaarten</P>
				<Label className="truncate flex-none">{yellowCards}</Label>
			</li>
			<li className="pb-2.5 w-full flex items-center gap-x-3">
				<P className="flex-auto truncate">Rode kaarten</P>
				<Label className="truncate flex-none">{redCards}</Label>
			</li>
			<li className="pb-2.5 w-full flex items-center gap-x-3">
				<P className="flex-auto truncate">Gespeeld van/tot</P>
				<Label className="truncate flex-none">{!endDate ? 'huidig' : `${startDate} - ${endDate}`}</Label>
			</li>
		</ul>
	);
};

export const SoccerField = () => {
	return (
		<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1118 718">
			<defs>
				<clipPath id="clip-Half">
					<rect width="1118" height="718" />
				</clipPath>
			</defs>
			<g id="Half" clipPath="url(#clip-Half)">
				<g id="Group_293" data-name="Group 293" transform="translate(1117 717.5) rotate(180)">
					<g id="Path_113" data-name="Path 113" transform="translate(0 -0.5)" fill="#149311">
						<path
							d="M 1115.5 716.8705444335938 L 1.5 716.8705444335938 L 1.5 1.499986529350281 L 1115.5 1.499986529350281 L 1115.5 716.8705444335938 Z"
							stroke="none"
						/>
						<path
							d="M 3 3 L 3 715.3705444335938 L 1114 715.3705444335938 L 1114 3 L 3 3 M 0 0 L 1117 0 L 1117 718.3705444335938 L 0 718.3705444335938 L 0 0 Z"
							stroke="none"
							fill="#fff"
						/>
					</g>
					<g
						id="Ellipse_1"
						data-name="Ellipse 1"
						transform="translate(437.191 599.516)"
						fill="none"
						stroke="#fff"
						strokeWidth="3"
					>
						<circle cx="121.742" cy="121.742" r="121.742" stroke="none" />
						<circle cx="121.742" cy="121.742" r="120.242" fill="none" />
					</g>
					<g
						id="Rectangle_2"
						data-name="Rectangle 2"
						transform="translate(950.757) rotate(90)"
						fill="none"
						stroke="#fff"
						strokeWidth="3"
					>
						<rect width="335.858" height="783.757" stroke="none" />
						<rect x="1.5" y="1.5" width="332.858" height="780.757" fill="none" />
					</g>
					<g
						id="Rectangle_3"
						data-name="Rectangle 3"
						transform="translate(743.745 0.12) rotate(90)"
						fill="none"
						stroke="#fff"
						strokeWidth="3"
					>
						<rect width="124.321" height="372.963" stroke="none" />
						<rect x="1.5" y="1.5" width="121.321" height="369.963" fill="none" />
					</g>
					<g id="Path_2" data-name="Path 2" transform="translate(565.689 218.725) rotate(90)" fill="none">
						<path d="M6.207,3.1a3.1,3.1,0,1,0-3.1,3.1A3.1,3.1,0,0,0,6.207,3.1Z" stroke="none" />
						<path
							d="M 3.104862451553345 6.205955028533936 C 1.390652418136597 6.205955028533936 2.36862183555786e-06 4.816404819488525 2.36862183555786e-06 3.103614807128906 C 2.36862183555786e-06 1.389384865760803 1.390652418136597 4.891662683803588e-06 3.104862451553345 4.891662683803588e-06 C 4.816492557525635 4.891662683803588e-06 6.207142353057861 1.389384865760803 6.207142353057861 3.103614807128906 C 6.207142353057861 4.816404819488525 4.816492557525635 6.205955028533936 3.104862451553345 6.205955028533936 Z"
							stroke="none"
							fill="#fff"
						/>
					</g>
					<path
						id="Path_3"
						data-name="Path 3"
						d="M0,300A191.476,191.476,0,0,0,72.241,150,191.473,191.473,0,0,0,0,0"
						transform="translate(710.943 333.514) rotate(90)"
						fill="none"
						stroke="#fff"
						strokeWidth="3"
					/>
				</g>
			</g>
		</svg>
	);
};

const PlayerPage: NextPage<PlayerPageProps> = ({
	player,
	error,
	passportData,
	careerStatsData,
	seasonStatsData,
	currentYearCareerStats,
	activeTeam,
	primaryMenuItems,
	sponsors,
}) => {
	const memoizedPassportEntries = useMemo(() => Object.entries(passportData || {}), [passportData]);
	const memoizedSeasonStatsEntries = useMemo(() => Object.entries(seasonStatsData || {}), [seasonStatsData]);
	const memoizedCurrentYearCareerStatsEntries = useMemo(
		() => Object.entries(currentYearCareerStats || {}),
		[currentYearCareerStats],
	);
	const memoizedActiveTeamEntries = useMemo(() => Object.entries(activeTeam || {}), [activeTeam]);

	if (error) {
		// handle error
		return <div>Error: {error}</div>;
	}

	const pageTitle = `${player.title} | ${config.siteName}`;
	const meta = player.title || 'Spelerspagina van de Keuken Kampioen Divisie.';

	const positionPlayer = player.passport?.position ?? '';

	const renderPositionClassName = (position: string) => {
		switch (position) {
			case 'Goalkeeper':
				return '-translate-x-1/2 -translate-y-14 top-full';
			case 'Defender':
				return '-translate-x-1/2 -translate-y-1/2 top-2/3';
			case 'Midfielder':
				return '-translate-x-1/2 -translate-y-1/2';
			case 'Attacker':
				return '-translate-x-1/2 -translate-y-1/2 top-1/4';
			default:
				return '-translate-x-1/2 -translate-y-1/2';
		}
	};

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<div className="relative overflow-hidden">
				<Image
					src={player.squad?.contestantPoster?.large || ''}
					alt="Picture"
					width={1920}
					height={430}
					className="object-cover h-full lg:h-[420px] w-full"
				/>
				<div className="kkd-gradient-red-overlay-vertical h-full w-full"></div>
				<Container>
					<div className="absolute top-1/2 transform -translate-y-1/2 flex flex-col">
						<div className="flex items-center">
							<div className="flex-grow">
								<H2 className="text-white mb-2">{player.squad?.contestantClubName}</H2>
								<Label className="text-white">{player.squad?.contestantVenueName}</Label>
							</div>
							{player.squad?.contestantLogo && (
								<Image
									src={player.squad.contestantLogo}
									alt={player.squad.contestantClubName}
									width={120}
									height={120}
									className="ml-8"
								/>
							)}
						</div>
					</div>
				</Container>
			</div>
			<Container className="pt-6 lg:pt-12 lg:pb-12 xl:pb-16">
				<div className="flex flex-col lg:flex-row">
					{/* Sidebar */}
					<div className="w-full lg:w-3/12 lg:mb-12 h-auto">
						<div className="w-full mb-8">
							<div className={`rounded-md flex flex-col bg-white w-full lg:max-w-xs`}>
								<div className="w-full bg-red-500 relative h-[220px] bg-[url('/images/shield.png')] bg-no-repeat bg-right-bottom  bg-contain">
									<div className="absolute bottom-0 right-0">
										<Image
											src={player.playerPhoto || '/images/player-profile.png'}
											alt={player.title}
											width={200}
											height={200}
											className="h-[200px] w-auto"
										/>
									</div>
									<div className="kkd-gradient-red-overlay-y absolute bottom-0 left-0"></div>

									<div className="absolute bottom-6 left-6">
										<Text className="text-7xl text-white font-bold">
											{passportData?.Rugnummer || ''}
										</Text>
									</div>
								</div>
								<div className="px-4 py-4 flex">
									<ul role="list" className="w-full">
										{memoizedPassportEntries.map(([key, value], index) => (
											<li key={index} className="pb-2.5 w-full">
												<div className="flex items-center gap-x-3">
													<P className="flex-none w-[33%] truncate">{key}</P>
													<Label className="truncate flex-1"> {value}</Label>
												</div>
											</li>
										))}
									</ul>
								</div>
							</div>
						</div>
					</div>

					{/* Main content */}
					<div className="flex-1 w-full lg:w-9/12 lg:ml-6">
						<H3 className="hidden lg:block">{player.title}</H3>
						<div className="flex flex-col lg:flex-row top-section gap-4 mb-8 ">
							{player.shields && player.shields.length > 0 && (
								<>
									<div className="lg:w-8/12 mt-6">
										<H5 className="text-left mb-4">Prestaties</H5>
										<div className="rounded-md flex flex-row bg-white mr-6 overflow-x-scroll">
											{player.shields.map((shield, index) => (
												<div
													className="p-4 flex flex-col items-center justify-start gap-2 shrink-0"
													key={index}
												>
													<Label className="mb-0">{shield.category}</Label>
													<img
														src={`../images/player/schild_${(
															shield.type || ''
														).toLowerCase()}.png`}
														alt="shield"
														className="h-24 w-24 shrink-0"
													/>
													<P className="!font-bold !mb-0">{shield.year}</P>
													<P className="-mt-2 !text-sm">{shield.period}</P>
												</div>
											))}
										</div>
									</div>
									<div className="lg:w-4/12">
										<H5 className="text-left mb-4">Huidig team</H5>
										{memoizedActiveTeamEntries.length > 0 ? (
											<div className="rounded-md flex flex-col bg-white w-full ">
												<div className="px-4 py-4">
													<ul role="list" className="w-full">
														{memoizedActiveTeamEntries.map(([key, value], index) => (
															<li key={index} className="pb-2.5 w-full">
																<div className="flex items-center gap-x-3">
																	<P className="truncate flex-auto ">{key}</P>
																	<Label className="truncate flex-none ">
																		{value}
																	</Label>
																</div>
															</li>
														))}
													</ul>
												</div>
											</div>
										) : (
											<P>Geen statistieken voor het huidige seizoen beschikbaar.</P>
										)}
									</div>
								</>
							)}
						</div>

						<div className="bottom-section flex flex-col lg:flex-row w-full">
							<div className="w-full lg:w-8/12">
								<div className="w-full current-season mb-8">
									<H5 className="text-left mb-4">Huidig seizoen</H5>
									{memoizedCurrentYearCareerStatsEntries.length > 0 ? (
										<div className="rounded-md flex flex-col bg-white w-full">
											<div className="p-4">
												<ul role="list" className="flex">
													{memoizedCurrentYearCareerStatsEntries.map(([key, value], index) =>
														value ? (
															<li key={index} className="flex-1">
																<div className="flex flex-col gap-x-4 px-1">
																	<Label className="mb-1 flex items-end text-xs ">
																		{key}
																	</Label>
																	<H2 className="!leading-none">{value}</H2>
																</div>
															</li>
														) : (
															<li key={index} className="flex-1">
																<div className="flex flex-col gap-x-4 px-1">
																	<Label className="mb-1 flex items-end text-xs ">
																		{key}
																	</Label>
																	<H2 className="!leading-none">0</H2>
																</div>
															</li>
														),
													)}
												</ul>
											</div>
										</div>
									) : (
										<P>Geen statistieken voor het huidige seizoen beschikbaar.</P>
									)}
								</div>

								<div className="season-stats mb-8">
									<H5 className="text-left mb-4">Statistieken</H5>
									{memoizedSeasonStatsEntries.length > 0 ? (
										<ul role="list" className="grid grid-cols-3 gap-4">
											{memoizedSeasonStatsEntries.map(([key, value], index) =>
												value ? (
													<li key={index} className="">
														<div className="flex flex-col bg-white rounded p-4">
															<Label className="mb-1 flex items-end text-xs">{key}</Label>
															<H2 className="!leading-none">{value}</H2>
														</div>
													</li>
												) : null,
											)}
										</ul>
									) : (
										<P>Geen seizoen statistieken beschikbaar.</P>
									)}
								</div>
							</div>
							<div className="w-full lg:w-4/12 lg:ml-8">
								{player.shields && player.shields.length === 0 ? (
									<div className="mb-4">
										<H5 className="text-left mb-4">Huidig team</H5>
										{memoizedActiveTeamEntries.length > 0 ? (
											<div className="rounded-md flex flex-col bg-white w-full ">
												<div className="px-4 py-4">
													<ul role="list" className="w-full">
														{Object.entries(activeTeam).map(([key, value], index) => (
															<li key={index} className="pb-2.5 w-full">
																<div className="flex items-center gap-x-3">
																	<P className="truncate flex-auto ">{key}</P>
																	<Label className="truncate flex-none ">
																		{value}
																	</Label>
																</div>
															</li>
														))}
													</ul>
												</div>
											</div>
										) : (
											<P>Geen statistieken voor het huidige seizoen beschikbaar.</P>
										)}
									</div>
								) : null}

								<div className="mb-4">
									<H5 className="text-left mb-4">Positie</H5>
									<div className="relative">
										<SoccerField />
										<div
											className={classNames(
												'absolute top-1/2 left-1/2 bg-white rounded-full w-12 h-12 flex items-center justify-center transform ',
												renderPositionClassName(positionPlayer),
											)}
										>
											{passportData?.Rugnummer || '?'}
										</div>
									</div>
								</div>
								<div className="mb-4">
									<H5 className="text-left mb-4">{`Carrière in de Eerste Divisie`}</H5>
									{careerStatsData?.length === 0 ? (
										<P>Geen carrière statistieken beschikbaar.</P>
									) : (
										<div className="rounded-md flex flex-col bg-white w-full">
											<div className="">
												{careerStatsData?.slice(1).map((item, index) => (
													<Disclosure key={index}>
														{({ open }) => (
															<>
																<Disclosure.Button className="flex w-full justify-between px-4 py-4 text-left text-black-500 hover:cursor-pointer border-b border-gray-200">
																	<H6>{item.tournamentCalendarName}</H6>
																	<ChevronUpIcon
																		className={`${
																			open
																				? 'rotate-180 transform transition-all'
																				: ' transition-all'
																		} h-6 w-6 text-black-500`}
																	/>
																</Disclosure.Button>
																<Disclosure.Panel>
																	<CareerData
																		goals={item.goals || 0}
																		appearances={item.appearances || 0}
																		contestantName={item.contestantName || ''}
																		assists={item.assists || 0}
																		yellowCards={item.yellowCards || 0}
																		redCards={item.redCards || 0}
																		startDate={item.startDate || ''}
																		endDate={item.endDate || ''}
																	/>
																</Disclosure.Panel>
															</>
														)}
													</Disclosure>
												))}
											</div>
										</div>
									)}
								</div>
							</div>
						</div>
					</div>
				</div>
			</Container>
		</Layout>
	);
};

export default PlayerPage;

export const getStaticPaths: GetStaticPaths = async () => {
	return { paths: [], fallback: 'blocking' };
};

export const getStaticProps: GetStaticProps = async (context) => {
	const primaryMenuItems = await getPrimaryMenuItems();
	const sponsorData = await fetchAPI<{ sponsors: { nodes: any[] } }>(
		`
		query {
			sponsors(first: 20) {
				nodes {
					title
					sponsor_fields {
						subtitle
						websiteUrl
						mainSponsor
						invertedImage {
							sourceUrl
						}
					}
				}
			}
		}
	`,
	);

	try {
		const { slug } = context.params!;
		const res = await fetch(`${config.apiUrl}/wp-json/statsperform/v1/player/${slug}`);
		const data: IPlayer = await res.json();

		const rankingsRes = await fetch(`${config.apiUrl}/wp-json/statsperform/v1/standings`);
		const rankingsData: IStandings[] = await rankingsRes.json();

		const passportData = getPassportData(data) || {};
		const careerStatsData = getCareerStats(data);
		const seasonStatsData = getSeasonStats(data) || {};
		const currentYearCareerStats = getCurrentYearCarreerStats(data) || {};
		const activeTeam = rankingsData ? getActiveTeamData(data, rankingsData) : {};

		return {
			props: {
				sponsors: sponsorData.sponsors.nodes,
				primaryMenuItems,
				player: data,
				passportData,
				careerStatsData,
				seasonStatsData,
				currentYearCareerStats,
				activeTeam,
			},
			revalidate: 300,
		};
	} catch (error) {
		console.error('Error fetching player data:', error);

		return {
			props: {
				error: (error as Error).message || 'Unexpected error occurred',
			},
		};
	}
};
